import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { filter } from 'lodash';
import PropTypes from 'prop-types';

import { buildMessage } from '@/global';

export const context = createContext(null);

export const useAppContext = () => {
  const ctx = useContext(context);
  if (!ctx) throw new Error('Context must be used inside a provider');
  return ctx;
};

const stopTimer = (message) => {
  if (message && message.timeoutId) {
    clearTimeout(message.timeoutId);
    delete message?.timeoutId;
  }

  return true;
};

export const ContextProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState();
  const [email, setEmail] = useState();
  const [username, setUsername] = useState();
  const [passVars, setPassVars] = useState({
    passHas8To15: false,
    passHasNumber: false,
    passHasUpperCase: false,
    passHasLowerCase: false,
    passHasSpecial: false,
    passDoesntContainUserIdString: false,
    passDoesntContainSpaces: false,
  });
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [sessionCredentials, setSessionCredentials] = useState({
    userId: '',
    password: '',
  }); // track credentials the user enters on the login screen
  const [invalidPasswordCount, setInvalidPasswordCount] = useState(0);
  const [invalidPasswords, setInvalidPasswords] = useState([]); // user passwords that fail the FR password policy on the back end.
  const [invalidNewEmails, setInvalidNewEmails] = useState([]); // keep track of invalid emails for email verification
  const [takenEmails, setTakenEmails] = useState([]); // keep track of taken emails from user reg
  const [blacklistedEmails, setBlacklistedEmails] = useState([]); // keep track of blacklisted emails from user reg
  const [takenUserIds, setTakenUserIds] = useState([]); // keep track of taken user ids from user reg
  const [invalidUserIds, setInvalidUserIds] = useState([]); // keep track of any invalid user ids from user reg
  const [invalidHotpSmsNumbers, setInvalidHotpSmsNumbers] = useState([]); // keep track of invalid sms hotp numbers for 2fa enrollment
  const [invalidHotpTtsNumbers, setInvalidHotpTtsNumbers] = useState([]); // keep track of invalid tts hotp numbers for 2fa enrollment
  const [queryParams, setQueryParams] = useState({});
  const [securityQuestionsOptions, setSecurityQuestionsOptions] = useState();
  const [userApiResponse, setUserApiResponse] = useState(); // should contain user api response for current user
  // Used exclusively for login next steps logic
  const [renderLoginNextSteps, setRenderLoginNextSteps] = useState(false);
  const [currentNextStepsPage, setCurrentNextStepsPage] = useState('');
  const [userIdString, setUserIdString] = useState('');

  const [prevStep, setPrevStep] = useState(); // previous step
  const [nextStep, setNextStep] = useState(); // next step as returned from AM
  const [stepToRender, setStepToRender] = useState(); // step to render on UI
  const [stepToSubmit, setStepToSubmit] = useState(); // step for submission
  const [messages, setMessages] = useState([]);

  // oaa agreement details for admin user
  const [oaaOrgsResponse, setOaaOrgsResponse] = useState();
  const [selectedOAAOrgIds, setSelectedOAAOrgIds] = useState();

  const addSelectedOAAOrgId = useCallback(
    (value) => {
      const updatedOrgIds = [...selectedOAAOrgIds, value];
      setSelectedOAAOrgIds([...updatedOrgIds]);
    },
    [selectedOAAOrgIds]
  );

  const removeSelectedOAAOrgId = useCallback(
    (value) => {
      const filteredOrgIds = filter(selectedOAAOrgIds, (orgId) => orgId !== value);
      setSelectedOAAOrgIds([...filteredOrgIds]);
    },
    [selectedOAAOrgIds]
  );

  // used for invitations UI navigation
  const [userHasInvites, setUserHasInvites] = useState(false);

  // Added because backend logic to save to user fact table is not complete
  // Once complete it will be possible to submit the accepted status on login
  const [userAgreementStatus, setUserAgreementStatus] = useState({
    essentialsAgreement: false,
  });

  const updateAuthIndexValue = useCallback(
    (value) => {
      setQueryParams({
        ...queryParams,
        authIndexValue: value,
      });
    },
    [queryParams]
  );

  const resetPassVars = useCallback(() => {
    setPassVars({
      passHas8To15: false,
      passHasNumber: false,
      passHasUpperCase: false,
      passHasLowerCase: false,
      passHasSpecial: false,
      passDoesntContainUserIdString: false,
      passDoesntContainSpaces: false,
      passMatches: false,
    });
  }, []);

  const incrementInvalidPasswordCount = useCallback(
    () => setInvalidPasswordCount(invalidPasswordCount + 1),
    [invalidPasswordCount]
  );

  const addInvalidNewEmail = useCallback(
    (value) => {
      // check if email has already been added
      const emailExists = invalidNewEmails.indexOf(value) >= 0;

      // only add the new email if it doesn't already exist in the array
      if (!emailExists) {
        setInvalidNewEmails([...invalidNewEmails, value]);
      }
    },
    [invalidNewEmails]
  );

  const addTakenEmail = useCallback(
    (value) => {
      // check if email has already been added
      const emailExists = takenEmails.indexOf(value) >= 0;

      // only add the taken email if it doesn't already exist in the array
      if (!emailExists) {
        setTakenEmails([...takenEmails, value]);
      }
    },
    [takenEmails]
  );

  const addBlacklistedEmail = useCallback(
    (value) => {
      // check if email has already been added
      const emailExists = blacklistedEmails.indexOf(value) >= 0;

      // only add the blacklisted email if it doesn't already exist in the array
      if (!emailExists) {
        setBlacklistedEmails([...blacklistedEmails, value]);
      }
    },
    [blacklistedEmails]
  );

  const addTakenUserId = useCallback(
    (value) => {
      // check if user id has already been added
      const userIdExists = takenUserIds.indexOf(value) >= 0;

      // only add the taken user id if it doesn't already exist in the array
      if (!userIdExists) {
        setTakenUserIds([...takenUserIds, value]);
      }
    },
    [takenUserIds]
  );

  const addInvalidUserId = useCallback(
    (value) => {
      // check if user id has already been added
      const userIdExists = invalidUserIds.indexOf(value) >= 0;

      // only add the invalid user id if it doesn't already exist in the array
      if (!userIdExists) {
        setInvalidUserIds([...invalidUserIds, value]);
      }
    },
    [invalidUserIds]
  );

  const addInvalidHotpNumber = useCallback(
    (value, hotpType) => {
      // check if phone has already been added
      let phoneNumberExists = false;
      if (hotpType === 'sms') {
        phoneNumberExists = invalidHotpSmsNumbers.indexOf(value) >= 0;
      } else if (hotpType === 'tts') {
        phoneNumberExists = invalidHotpTtsNumbers.indexOf(value) >= 0;
      }

      // only add the new phone if it doesn't already exist in the array
      if (!phoneNumberExists) {
        if (hotpType === 'sms') {
          setInvalidHotpSmsNumbers([...invalidHotpSmsNumbers, value]);
        } else if (hotpType === 'tts') {
          setInvalidHotpTtsNumbers([...invalidHotpTtsNumbers, value]);
        }
      }
    },
    [invalidHotpSmsNumbers, invalidHotpTtsNumbers]
  );

  // message functions
  const removeMessage = useCallback(
    (id) => {
      const toRemove = messages.find((message) => message.id === id);
      if (toRemove) {
        const newMessagesArray = messages.filter((message) => message.id !== id) || [];
        stopTimer(toRemove);
        setMessages([...newMessagesArray]);
      }
    },
    [messages]
  );

  const addMessage = useCallback(
    (val) => {
      let newMessage = buildMessage(val);
      let newMessagesArray = messages;

      // check if new message already exists
      const existingMessage = messages.find((message) => message.id === newMessage.id);
      if (existingMessage) {
        newMessage = existingMessage;
        stopTimer(newMessage);
      } else {
        newMessagesArray = [newMessage, ...messages];
      }

      // set the new message array
      setMessages([...newMessagesArray]);

      // timeout until message should remove itself
      setTimeout(() => {
        removeMessage(newMessage.id);
      }, newMessage.timeout);
    },
    [messages, removeMessage]
  );

  const contextValue = useMemo(
    () => ({
      loading,
      setLoading,
      isAuthenticated,
      setIsAuthenticated,
      user,
      setUser,
      email,
      setEmail,
      username,
      setUsername,
      sessionCredentials,
      setSessionCredentials,
      passVars,
      setPassVars,
      resetPassVars,
      isPasswordFocused,
      setIsPasswordFocused,
      invalidPasswordCount,
      setInvalidPasswordCount,
      incrementInvalidPasswordCount,
      invalidPasswords,
      setInvalidPasswords,
      invalidNewEmails,
      setInvalidNewEmails,
      addInvalidNewEmail,
      takenEmails,
      setTakenEmails,
      addTakenEmail,
      blacklistedEmails,
      setBlacklistedEmails,
      addBlacklistedEmail,
      takenUserIds,
      setTakenUserIds,
      addTakenUserId,
      invalidUserIds,
      setInvalidUserIds,
      addInvalidUserId,
      invalidHotpSmsNumbers,
      setInvalidHotpSmsNumbers,
      invalidHotpTtsNumbers,
      setInvalidHotpTtsNumbers,
      addInvalidHotpNumber,
      queryParams,
      setQueryParams,
      securityQuestionsOptions,
      setSecurityQuestionsOptions,
      userApiResponse,
      setUserApiResponse,
      updateAuthIndexValue,
      prevStep,
      setPrevStep,
      nextStep,
      setNextStep,
      stepToRender,
      setStepToRender,
      stepToSubmit,
      setStepToSubmit,
      messages,
      setMessages,
      oaaOrgsResponse,
      setOaaOrgsResponse,
      selectedOAAOrgIds,
      setSelectedOAAOrgIds,
      addSelectedOAAOrgId,
      removeSelectedOAAOrgId,
      userHasInvites,
      setUserHasInvites,
      removeMessage,
      addMessage,
      renderLoginNextSteps,
      setRenderLoginNextSteps,
      currentNextStepsPage,
      setCurrentNextStepsPage,
      userIdString,
      setUserIdString,
      userAgreementStatus,
      setUserAgreementStatus,
    }),
    [
      loading,
      setLoading,
      isAuthenticated,
      setIsAuthenticated,
      user,
      setUser,
      email,
      setEmail,
      username,
      setUsername,
      sessionCredentials,
      setSessionCredentials,
      passVars,
      setPassVars,
      resetPassVars,
      isPasswordFocused,
      setIsPasswordFocused,
      invalidPasswordCount,
      setInvalidPasswordCount,
      incrementInvalidPasswordCount,
      invalidPasswords,
      setInvalidPasswords,
      invalidNewEmails,
      setInvalidNewEmails,
      addInvalidNewEmail,
      takenEmails,
      setTakenEmails,
      addTakenEmail,
      blacklistedEmails,
      setBlacklistedEmails,
      addBlacklistedEmail,
      takenUserIds,
      setTakenUserIds,
      addTakenUserId,
      invalidUserIds,
      setInvalidUserIds,
      addInvalidUserId,
      invalidHotpSmsNumbers,
      setInvalidHotpSmsNumbers,
      invalidHotpTtsNumbers,
      setInvalidHotpTtsNumbers,
      addInvalidHotpNumber,
      queryParams,
      setQueryParams,
      securityQuestionsOptions,
      setSecurityQuestionsOptions,
      userApiResponse,
      setUserApiResponse,
      updateAuthIndexValue,
      prevStep,
      setPrevStep,
      nextStep,
      setNextStep,
      stepToRender,
      setStepToRender,
      stepToSubmit,
      setStepToSubmit,
      messages,
      setMessages,
      oaaOrgsResponse,
      setOaaOrgsResponse,
      selectedOAAOrgIds,
      setSelectedOAAOrgIds,
      addSelectedOAAOrgId,
      removeSelectedOAAOrgId,
      userHasInvites,
      setUserHasInvites,
      removeMessage,
      addMessage,
      renderLoginNextSteps,
      setRenderLoginNextSteps,
      currentNextStepsPage,
      setCurrentNextStepsPage,
      userIdString,
      setUserIdString,
      userAgreementStatus,
      setUserAgreementStatus,
    ]
  );

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};

ContextProvider.propTypes = {
  children: PropTypes.node,
};
